/* eslint-disable react/jsx-no-useless-fragment */
import { Col, Row, PopupHint } from '@op/opds.opux';
import { useEffect, useState } from 'react';
import { font, color, space } from '@op/opds-tokens';
import { Message, useMessageGetter } from '@messageformat/react';
import { numberToFinnishString } from '../util/numberFormatter';
import ProfitAndSavingsGraph from './ProfitAndSavingsGraph';
import { ContentWrapper } from './ContentWrapper';

interface Props {
  monthlySaving: number;
  savingTime: number;
  initialSaving: number;
  profitExpectation: number;
  isBannerLayout: boolean;
  isPersonCustomer: boolean;
  // eslint-disable-next-line no-unused-vars, react/require-default-props
  onValuesChange?: (finalSum: number, totalSaving: number, bonusSum: number) => void;
}

const generateRandomFloat = (lower: number, upper: number): number => {
  return Math.random() * (upper - lower) + lower;
};

const countProfitExceptationMonth = (profitExceptation12Month: number): number => {
  return (1 + profitExceptation12Month / 100) ** (1 / 12) - 1;
};

const createSavingArray = (
  initialSaving: number,
  savingTime: number,
  monthlySaving: number,
): number[] => {
  let totalSavings = initialSaving;
  return [...Array(savingTime * 12 + 1)].map((_, index) => {
    if (index === 0) {
      return initialSaving;
    }
    totalSavings += monthlySaving;
    return totalSavings;
  });
};

const createProfitArray = (
  profitExpectation: number,
  initialSaving: number,
  savingTime: number,
  monthlySaving: number,
): number[] => {
  const kktuotto = countProfitExceptationMonth(profitExpectation);
  let finalSavingsAndProfit = initialSaving;
  return [...Array(savingTime * 12 + 1)].map((_, index) => {
    if (index === 0) {
      return initialSaving;
    }
    finalSavingsAndProfit += monthlySaving;
    finalSavingsAndProfit *= 1 + kktuotto;
    return finalSavingsAndProfit;
  });
};

const ProfitAndSavingsWrapper = (children: JSX.Element[]): JSX.Element => {
  return <div className="savingscalculator-box">{children}</div>;
};

const ProfitAndSavingsComponent = (props: Props): JSX.Element => {
  const {
    monthlySaving,
    savingTime,
    initialSaving,
    profitExpectation,
    isBannerLayout,
    isPersonCustomer,
    onValuesChange,
  } = props;
  const [savingsGraphArray, setSavingsGraphArray] = useState<number[]>([]);
  const [profitGraphArray, setProfitGraphArray] = useState<number[]>([]);
  const [totalSaving, setTotalSaving] = useState(0);
  const [finalSum, setFinalSum] = useState(0);
  const [bonusSum, setBonusSum] = useState(0);
  const getTranslations = useMessageGetter('');
  const opBonusPercent = 0.4; // 0.4% bonusprosentti ennen veroja

  useEffect(() => {
    const countOPBonus = (): void => {
      const profitArray = createProfitArray(
        profitExpectation,
        initialSaving,
        savingTime,
        monthlySaving,
      );
      let totalBonus = 0;
      let previousItem = profitArray[0];
      profitArray.forEach((item) => {
        const averageBalance = (item + previousItem) / 2;
        const monthlyBonusRate = (opBonusPercent / 100 / 12) * 0.7; // 0.7 = after tax
        totalBonus += averageBalance * monthlyBonusRate;
        previousItem = item;
      });
      setBonusSum(totalBonus);
    };
    countOPBonus();
  }, [monthlySaving, savingTime, initialSaving, profitExpectation]);

  useEffect(() => {
    const createGraphDataWithVolatility = (
      array: number[],
      volatility: number,
    ): number[] => {
      const graphData: number[] = [];
      array.forEach((item, index) => {
        if (index % 5 === 0 && index < array.length - 1) {
          const v = generateRandomFloat(
            (volatility / 2 / 100) * -1,
            volatility / 2 / 100,
          );
          graphData.push(Math.floor(array[index] * (volatility ? 1 + v : 1)));
        } else if (index === array.length - 1) {
          graphData.push(array[index]);
        }
      });
      return graphData;
    };

    const laskeTuottodatasetti = (): void => {
      const savingArray = createSavingArray(initialSaving, savingTime, monthlySaving);
      setTotalSaving(savingArray[savingArray.length - 1]);

      const profitArray = createProfitArray(
        profitExpectation,
        initialSaving,
        savingTime,
        monthlySaving,
      );
      setFinalSum(profitArray[profitArray.length - 1]);

      const graphData = createGraphDataWithVolatility(profitArray, profitExpectation * 4);
      const roundedSavingGraphData = createGraphDataWithVolatility(savingArray, 0);

      setProfitGraphArray(graphData);
      setSavingsGraphArray(roundedSavingGraphData);
    };
    laskeTuottodatasetti();
  }, [monthlySaving, savingTime, initialSaving, profitExpectation]);

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(finalSum, totalSaving, bonusSum);
    }
  }, [finalSum, totalSaving, bonusSum, onValuesChange]);

  return (
    <>
      <ContentWrapper condition={!isBannerLayout} wrapper={ProfitAndSavingsWrapper}>
        <Row>
          <Col sm={12} md={6} lg={6} className="savingscalculator-labels-container">
            <p className="savingscalculator-labels" style={{ fontSize: font.size[5] }}>
              <Message id="profitAndSavings_estimatedSavings" />
            </p>
            <p
              className="savingscalculator-profit-rows savingscalculator-total"
              style={{
                fontSize: font.size[8],
                color: color.text.highlight.large,
              }}
            >
              {numberToFinnishString(finalSum)} €
            </p>
          </Col>
          <Col sm={12} md={6} lg={6} className="savingscalculator-labels">
            <p
              className="savingscalculator-profit-values"
              style={{ marginBottom: space.xxs }}
            >
              <Message id="profitAndSavings_savings" />
              <span
                className="savingscalculator-profit-rows"
                style={{
                  fontSize: font.size[4],
                  fontWeight: font.weight.light,
                  paddingLeft: space.large,
                }}
              >
                {numberToFinnishString(totalSaving)} €
              </span>
            </p>

            <p
              className="savingscalculator-profit-values"
              style={{ marginBottom: space.xxs }}
            >
              <Message id="profitAndSavings_estimatedProfit" />
              <span
                className="savingscalculator-profit-rows"
                style={{
                  fontSize: font.size[4],
                  fontWeight: font.weight.light,
                  paddingLeft: space.large,
                }}
              >
                {numberToFinnishString(finalSum - totalSaving)} €
              </span>
            </p>
          </Col>
          {isPersonCustomer && (
            <Col
              sm={12}
              md={6}
              lg={6}
              className="savingscalculator-labels"
              style={{ paddingBottom: space.default }}
            >
              <div style={{ marginTop: space.xxxs }}>
                <span>
                  <Message id="profitAndSavings_opBonus" />
                  <span className="savingcalculator-bonus">
                    +{numberToFinnishString(bonusSum)} €
                  </span>
                </span>
                <PopupHint
                  label={getTranslations('bonusHint_label')}
                  style={{
                    display: 'inline-block',
                    marginLeft: space.xxs,
                    verticalAlign: 'middle',
                  }}
                >
                  <Message id="profitAndSavings_bonusDescription" />
                </PopupHint>
              </div>
            </Col>
          )}
        </Row>
        <ProfitAndSavingsGraph
          savingsGraphArray={savingsGraphArray}
          profitGraphArray={profitGraphArray}
        />
        <Row end>
          <p style={{ float: 'right' }} aria-hidden="true">
            {savingTime} <Message id="profitAndSavings_years" />
          </p>
        </Row>
      </ContentWrapper>
      {isBannerLayout && <hr />}
    </>
  );
};

export default ProfitAndSavingsComponent;
